import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import * as authRepository from '@/auth/data/authRepository';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelContainer } from '@/ui/pixel/PixelContainer';

type Status = 'idle' | 'busy' | 'ok' | 'error';

export function VerifyEmailScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [status, setStatus] = useState<Status>(token ? 'busy' : 'error');
  const [message, setMessage] = useState(token ? '' : t('authVerifyMissingToken'));

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      try {
        await authRepository.verifyEmail(token);
        if (!cancelled) {
          setStatus('ok');
          setMessage(t('authVerifySuccess'));
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
          setMessage(t('authVerifyFailed'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  return (
    <PageChrome title={t('authVerifyEmailTitle')}>
      <div className="flex flex-1 items-center justify-center px-7">
        <div className="w-full max-w-[420px]">
          <PixelContainer
            radius={3}
            pixelScale={2}
            padding="20px"
            backgroundColor={colors.surfaceContainerHighest}
            borderColor={colors.onSurface}
          >
            <p className="leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
              {status === 'busy' ? t('authVerifyWorking') : message}
            </p>
            {status === 'ok' && (
              <Link
                to={appRoutes.home}
                className="mt-5 inline-block leading-tight underline"
                style={{ fontSize: 16, color: colors.onSurface }}
              >
                {t('authVerifyBackToGame')}
              </Link>
            )}
          </PixelContainer>
        </div>
      </div>
    </PageChrome>
  );
}
